import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, User, Store, ArrowRight, LogIn } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../contexts/AuthContext";
import { usePiAuth } from "../contexts/PiAuthContext";
import { NavbarLight } from "../components/Navbar";
import { Footer } from "../components/Footer";
import SEO from "../components/SEO";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";

const Login = () => {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const { signInWithPi, sdkReady, loading: piLoading } = usePiAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        await register({ email, password, name, business_name: businessName || undefined });
        toast.success("Account created! Welcome to EK Valleys.");
      } else {
        await login(email, password);
        toast.success("Welcome back!");
      }
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join(" ") : "Something went wrong";
      toast.error(msg);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#EFEDE5]">
      <SEO title={isRegister ? "Create Account" : "Log In"} description="Log in or create your EK Valleys account." />
      <NavbarLight />

      <div className="max-w-md mx-auto px-6 py-16">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-[#2C4A3B] flex items-center justify-center mx-auto mb-4">
            <LogIn className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-[#1A1915]" data-testid="auth-page-title">
            {isRegister ? "Create Your Account" : "Welcome Back"}
          </h1>
          <p className="text-[#5C5A52] mt-2">
            {isRegister ? "Join the EK Valleys community." : "Log in to your EK Valleys account."}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border-2 border-[#D1CFC5] rounded-xl p-6 space-y-4">
          {isRegister && (
            <>
              <div>
                <Label className="text-[#1A1915]">Full Name *</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5A52]" />
                  <Input value={name} onChange={e => setName(e.target.value)} className="pl-10 border-[#D1CFC5]" placeholder="Your name" required data-testid="auth-name" />
                </div>
              </div>
              <div>
                <Label className="text-[#1A1915]">Business Name</Label>
                <div className="relative mt-1">
                  <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5A52]" />
                  <Input value={businessName} onChange={e => setBusinessName(e.target.value)} className="pl-10 border-[#D1CFC5]" placeholder="Optional" data-testid="auth-business" />
                </div>
              </div>
            </>
          )}
          <div>
            <Label className="text-[#1A1915]">Email *</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5A52]" />
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} className="pl-10 border-[#D1CFC5]" placeholder="you@email.com" required data-testid="auth-email" />
            </div>
          </div>
          <div>
            <Label className="text-[#1A1915]">Password *</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5A52]" />
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} className="pl-10 border-[#D1CFC5]" placeholder={isRegister ? "Min 6 characters" : "Your password"} required data-testid="auth-password" />
            </div>
          </div>

          <Button type="submit" disabled={loading} className="w-full bg-[#2C4A3B] hover:bg-[#1A2F23] text-white font-semibold py-3" data-testid="auth-submit-btn">
            {loading ? "Please wait..." : isRegister ? "Create Account" : "Log In"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>

          <div className="text-center pt-2">
            <button type="button" onClick={() => setIsRegister(!isRegister)} className="text-sm text-[#2C4A3B] hover:underline" data-testid="auth-toggle">
              {isRegister ? "Already have an account? Log in" : "Don't have an account? Create one"}
            </button>
          </div>

          {!isRegister && (
            <div className="text-center">
              <Link to="/forgot-password" className="text-xs text-[#5C5A52] hover:text-[#1A1915]">Forgot password?</Link>
            </div>
          )}
        </form>

        {/* Pi Network Sign-In */}
        <div className="mt-4 bg-white border-2 border-[#D1CFC5] rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-[#7B4FC4] flex items-center justify-center">
              <span className="text-white font-bold text-sm">Pi</span>
            </div>
            <div>
              <p className="font-semibold text-[#1A1915] text-sm">Sign in with Pi Network</p>
              <p className="text-xs text-[#5C5A52]">{sdkReady ? "Pi Browser detected" : "Open in Pi Browser to use"}</p>
            </div>
          </div>
          <Button
            type="button"
            disabled={piLoading}
            className="w-full bg-[#7B4FC4] hover:bg-[#6B3FA4] text-white font-semibold py-3"
            data-testid="pi-signin-btn"
            onClick={async () => {
              try {
                await signInWithPi();
                toast.success("Signed in with Pi Network!");
                navigate("/dashboard");
              } catch (err) {
                toast.error(err?.message || "Pi sign-in failed. Make sure you're in the Pi Browser.");
              }
            }}
          >
            {piLoading ? "Connecting..." : "Sign in with Pi"}
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Login;
